import { useAuth } from "@faire/prototype-playground/contexts/AuthContext";
import { cn } from "@faire/prototype-playground/lib/utils";
import { isDevelopment } from "@faire/prototype-playground/platform/lib/env";
import { useState } from "react";
import { useHistory } from "react-router-dom";

import { CollapseIcon } from "./icons/CollapseIcon";
import { ExpandIcon } from "./icons/ExpandIcon";
import { HomeIcon } from "./icons/HomeIcon";
import { InfoIcon } from "./icons/InfoIcon";
import { LocaleIcon } from "./icons/LocaleIcon";
import { PressIcon } from "./icons/PressIcon";
import { SettingsIcon } from "./icons/SettingsIcon";
import { ShareIcon } from "./icons/ShareIcon";
import { StatesIcon } from "./icons/StatesIcon";
import { VariantsIcon } from "./icons/VariantsIcon";
import { PatternUseToolbarButton } from "./PatternUseToolbarButton";
import { FlyoutPanel } from "./types";

export const PanelButtons: React.FC<{
  hasVariants: boolean;
  hasStates: boolean;
  hasSettings: boolean;
  selected: FlyoutPanel;
  onSelect: (panel: FlyoutPanel) => void;
}> = ({ hasVariants, hasStates, hasSettings, selected, onSelect }) => {
  const history = useHistory();
  const { user } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const showShareButton = user || isDevelopment();

  return (
    <div>
      {isCollapsed ? (
        <button
          onClick={() => setIsCollapsed(false)}
          className="flex h-9 w-9 items-center justify-center text-[#666] transition-colors hover:text-(--slate-color-action-text-inverse)"
        >
          <ExpandIcon />
        </button>
      ) : (
        <>
          <button
            onClick={() => history.push("/")}
            className={getButtonCls(false, "blue")}
          >
            <PressIcon>
              <HomeIcon />
            </PressIcon>
          </button>
          <div className="mx-auto h-px w-5 bg-[#2a2a2a]" />
          <button
            onClick={() => onSelect("info")}
            className={getButtonCls(selected === "info", "amber")}
          >
            <PressIcon>
              <InfoIcon />
            </PressIcon>
          </button>
          <button
            onClick={() => onSelect("locale")}
            className={getButtonCls(selected === "locale", "emerald")}
            title="Locale preview"
          >
            <PressIcon>
              <LocaleIcon />
            </PressIcon>
          </button>
          <PatternUseToolbarButton />
          {hasVariants ? (
            <button
              onClick={() => onSelect("variants")}
              className={getButtonCls(selected === "variants", "violet")}
            >
              <PressIcon>
                <VariantsIcon />
              </PressIcon>
            </button>
          ) : null}
          {hasStates ? (
            <button
              onClick={() => onSelect("states")}
              className={getButtonCls(selected === "states", "rose")}
            >
              <PressIcon>
                <StatesIcon />
              </PressIcon>
            </button>
          ) : null}
          {hasSettings ? (
            <button
              onClick={() => onSelect("settings")}
              className={getButtonCls(selected === "settings", "cyan")}
            >
              <PressIcon>
                <SettingsIcon />
              </PressIcon>
            </button>
          ) : null}
          {showShareButton ? (
            <button
              onClick={() => onSelect("share")}
              className={getButtonCls(selected === "share", "sky")}
            >
              <PressIcon>
                <ShareIcon />
              </PressIcon>
            </button>
          ) : null}
          <div className="mx-auto h-px w-5 bg-[#2a2a2a]" />
          <button
            onClick={() => {
              setIsCollapsed(true);
              onSelect(null);
            }}
            className={getButtonCls(false, "red")}
          >
            <PressIcon>
              <CollapseIcon />
            </PressIcon>
          </button>
        </>
      )}
    </div>
  );
};

type Color =
  | "blue"
  | "amber"
  | "violet"
  | "emerald"
  | "cyan"
  | "rose"
  | "sky"
  | "red";

const getButtonCls = (active: boolean, color: Color) => {
  const m: Record<Color, { h: string; a: string }> = {
    blue: {
      h: "hover:bg-[#2b7fff]/15 hover:text-[#51a2ff]",
      a: "bg-[#2b7fff]/20 text-[#51a2ff]",
    },
    amber: {
      h: "hover:bg-[#fe9a00]/15 hover:text-[#ffb900]",
      a: "bg-[#fe9a00]/20 text-[#ffb900]",
    },
    violet: {
      h: "hover:bg-[#8e51ff]/15 hover:text-[#a684ff]",
      a: "bg-[#8e51ff]/20 text-[#a684ff]",
    },
    emerald: {
      h: "hover:bg-[#00bc7d]/15 hover:text-[#00d492]",
      a: "bg-[#00bc7d]/20 text-[#00d492]",
    },
    cyan: {
      h: "hover:bg-[#00b8db]/15 hover:text-[#00d3f2]",
      a: "bg-[#00b8db]/20 text-[#00d3f2]",
    },
    rose: {
      h: "hover:bg-[#ff2056]/15 hover:text-[#ff637e]",
      a: "bg-[#ff2056]/20 text-[#ff637e]",
    },
    sky: {
      h: "hover:bg-[#00a6f4]/15 hover:text-[#00bcff]",
      a: "bg-[#00a6f4]/20 text-[#00bcff]",
    },
    red: {
      h: "hover:bg-(--slate-color-red-500)/15 hover:text-(--slate-color-red-400)",
      a: "bg-(--slate-color-red-500)/20 text-(--slate-color-red-400)",
    },
  };
  const c = m[color];
  return cn(
    "flex h-9 w-9 items-center justify-center rounded-lg transition-colors",
    active ? c.a : `text-[#999] ${c.h}`
  );
};
